import {
   AlwaysCopiedPreviewEntity,
   BE_ONLY_ALWAYS_COPIED_ENTITIES,
   CopyEntity,
   ENTITY_FIELD_ACCESSOR,
   ExistingMode,
   PreviewEntity,
} from '../types/copy-paste.types';
import { CLIENT_CREATABLE_MAT_SETS, SubItem } from '../../../types/sub-item.types';

// Use a separator that cannot occur in field values to avoid key collisions.
export const KEY_SEPARATOR = '\u001F';

export const generatePartIdentifier = (subItem: SubItem) =>
   [
      subItem.part,
      subItem.matSetName,
      subItem.partDetail,
      subItem.materialGrade,
      subItem.id,
      subItem.parentId || 'no parent',
   ].join(KEY_SEPARATOR);

export const getSubItemMatchKey = (s: SubItem): string =>
   [s.partDetail, s.part, s.materialGrade, s.matSetName].join(KEY_SEPARATOR);

export const isEmpty = (val: unknown): boolean =>
   val === null || val === undefined || val === '';

export const formatFieldVal = (val: unknown): string =>
   isEmpty(val) ? '-' : String(val);

// Compare raw values so empty does not equal the literal '-'.
const fieldValsEqual = (a: unknown, b: unknown): boolean =>
   isEmpty(a) || isEmpty(b) ? isEmpty(a) === isEmpty(b) : String(a) === String(b);

export const canCreateMissingParts = (copiedParts: SubItem[]) =>
   copiedParts.some(
      (subItem) =>
         (CLIENT_CREATABLE_MAT_SETS as readonly string[]).includes(subItem.matSetName) &&
         subItem.clientCreated
   );

export const getWillSkip = (
   variant: 'existing' | 'new',
   existingMode: ExistingMode,
   createMissing: boolean,
   canCreate: boolean
): boolean => (variant === 'new' ? !(createMissing && canCreate) : existingMode === 'skip');

// Match backend behavior for when MaterialGradeId is copied.
export const getApplicableAlwaysCopiedEntities = (
   variant: 'existing' | 'new',
   existingMode: ExistingMode
): AlwaysCopiedPreviewEntity[] =>
   variant === 'new' || existingMode === 'overwrite' ? ['MaterialGradeId'] : [];

export const getChangedEntities = (
   srcPart: SubItem,
   destPart: SubItem | undefined,
   entities: PreviewEntity[],
   existingMode: ExistingMode
): PreviewEntity[] => {
   if (!destPart) return entities.filter((e) => !isEmpty(srcPart[ENTITY_FIELD_ACCESSOR[e]]));
   if (existingMode === 'skip') return [];

   return entities.filter((entity) => {
      const field = ENTITY_FIELD_ACCESSOR[entity];
      const srcVal = srcPart[field];
      const currentVal = destPart[field];

      return existingMode === 'overwrite'
         ? !fieldValsEqual(currentVal, srcVal)
         : isEmpty(currentVal) && !isEmpty(srcVal);
   });
};

export const buildCopyPayload = ({
   copiedParts,
   verifiedPartIds,
   verifiedItemNos,
   selectedCopyEntities,
   existingMode,
   createMissing,
}: {
   copiedParts: SubItem[];
   verifiedPartIds: string[];
   verifiedItemNos: string[];
   selectedCopyEntities: CopyEntity[];
   existingMode: ExistingMode;
   createMissing: boolean;
}) => {
   const finalPartsToCopy = copiedParts.filter((subItem) =>
      verifiedPartIds.includes(generatePartIdentifier(subItem))
   );

   return {
      itemsFrom: finalPartsToCopy.map((subItem) => ({
         itemNo: subItem.parentId,
         materialSetName: subItem.matSetName,
         id: subItem.id,
      })),
      itemNos: verifiedItemNos,
      pasteTypes: [...selectedCopyEntities, ...BE_ONLY_ALWAYS_COPIED_ENTITIES],
      existingMode,
      createMissing,
   };
};

export const countCopyResults = (res: {
   supplierMaterialCopyPaste: {
      itemsFrom: Array<{ itemNo: string; status: string }>;
      itemNos: Array<{ itemNo: string; status: string }>;
   };
}) => {
   const copiedCount = res.supplierMaterialCopyPaste.itemsFrom.filter(
      (item) => item.status === 'Ok'
   ).length;

   // Count created-missing as successful paste.
   const pastedCount = res.supplierMaterialCopyPaste.itemNos.reduce<Set<string>>((seen, item) => {
      if (item.status === 'Ok' || item.status === 'Created missing part') seen.add(item.itemNo);
      return seen;
   }, new Set()).size;

   return { copiedCount, pastedCount };
};
